import React, { useState, useEffect, lazy, Suspense } from 'react';
import SharedHelmet from ".././Helmet";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import imageUrl from "../../../images/ramadan.webp";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../firebaseConfig";
import moment from 'moment-hijri';
const BlinkingText = lazy(() => import('../../BlinkingText'));
const RelatedLinks = lazy(() => import('../RelatedLinks'));
const MainCalculatorsList = lazy(() => import('./MainCalculators'));
const TITLE = "Berapa Hari Lagi Ramadan 2027 - Hitung Mundur Ramadan";
const DESCRIPTION = "Berapa hari lagi Ramadan 2027? Hitung mundur bulan suci Ramadan 1445 H memberikan informasi akurat tentang kapan Ramadan dimulai, berapa hari lagi Ramadan, dan jumlah hari yang tersisa hingga bulan yang penuh berkah tiba. Ikuti hitung mundur Ramadan dan ketahui waktu yang tersisa hingga awal puasa dan perayaan bulan yang mulia. Temukan kapan Ramadan dan berapa hari lagi Ramadan dengan cara terbaik melalui hitung mundur bulan suci ini.";
const KEYWORDS = "Ramadan 2027, berapa hari lagi Ramadan 2027, berapa hari lagi Ramadan, hitung mundur Ramadan, berapa hari lagi Ramadan hitung mundur, berapa hari lagi puasa, sisa hari Ramadan, kapan Ramadan, puasa 2027, Ramadhan 2027, hitungan mundur Ramadan, berapa lama lagi Ramadan";
const OG_URL = "https://do-calculate.com/articles-id/ramadan/";
function Ramadan() {
    const [ramadanDates, setRamadanDates] = useState([
        // default value for 2027, will be replaced when DB data arrives
        { name: 'Ramadan 2027', date: new Date("2027-02-08T00:00:00") },
        { name: 'Ramadan 2028', date: new Date("2028-01-28T00:00:00") },
        { name: 'Ramadan 2029', date: new Date("2029-01-16T00:00:00") }
    ]);
    const nextRamadan = ramadanDates.find(d => d.name === 'Ramadan 2027')?.date || new Date("2027-02-18T00:00:00");
    const dayNameIndonesian = nextRamadan.toLocaleDateString('id-ID', { weekday: 'long' });
    useEffect(() => {
        async function fetchRamadanDates() {
            try {
                const datesCol = collection(db, "dates");
                const querySnapshot = await getDocs(datesCol);
                let fetchedRamadan2027Date = null;
                const newRamadanDates = [];
                querySnapshot.forEach((doc) => {
                    const data = doc.data();
                    if (data.ramadan) {
                        const date = data.ramadan.toDate ? data.ramadan.toDate() : new Date(data.ramadan);
                        if (date.getFullYear() === 2027) {
                            fetchedRamadan2027Date = date;
                            newRamadanDates.push({ name: 'Ramadan 2027', date: date });
                        }
                    }
                });
                if (fetchedRamadan2027Date) {
                    setRamadanDates(prevDates =>
                        prevDates.map(item =>
                            item.name === 'Ramadan 2027'
                                ? { ...item, date: fetchedRamadan2027Date }
                                : item
                        )
                    );
                } else {
                    console.warn("No Ramadan 2027 date found in DB. Using default.");
                }
            } catch (error) {
                console.error("Error fetching Ramadan dates:", error);
            }
        }
        fetchRamadanDates();
    }, []);
    const calculateTimeLeft = (targetDate) => {
        const now = new Date();
        const difference = targetDate - now;
        let timeLeft = {};
        if (difference > 0) {
            timeLeft = {
                days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                minutes: Math.floor((difference / 1000 / 60) % 60),
                seconds: Math.floor((difference / 1000) % 60)
            };
        }
        return timeLeft;
    };
    const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(nextRamadan));
    useEffect(() => {
        const timer = setTimeout(() => {
            setTimeLeft(calculateTimeLeft(nextRamadan));
        }, 1000);
        return () => clearTimeout(timer);
    }, [timeLeft, nextRamadan]);
    // Generate Schema.org structured data for Ramadan Article
    const generateSchemaData = () => {
        // NewsArticle Schema
        const articleSchema = {
            "@context": "https://schema.org",
            "@type": "Article",
            "mainEntityOfPage": OG_URL,
            "headline": TITLE,
            "description": DESCRIPTION,
            "image": [
                {
                    "@type": "ImageObject",
                    "url": `https://do-calculate.com${imageUrl}`,
                    "width": 800,
                    "height": 450
                }
            ],
            "author": {
                "@type": "Organization",
                "name": "Do Calculate",
                "url": "https://do-calculate.com"
            },
            "publisher": {
                "@type": "Organization",
                "name": "Do Calculate",
                "url": "https://do-calculate.com",
                "logo": {
                    "@type": "ImageObject",
                    "url": "https://do-calculate.com/logo.png"
                }
            },
            "datePublished": "2027-01-10T00:00:00Z",
            "dateModified": new Date().toISOString()
        };
        // WebPage Schema
        const webPageSchema = {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": TITLE,
            "description": DESCRIPTION,
            "url": OG_URL
        };
        // FAQ Schema
        const faqSchema = {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
                {
                    "@type": "Question",
                    "name": "Berapa hari lagi Ramadan 2027?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": `Diperkirakan bulan Ramadan 2027 dimulai hari ${dayNameIndonesian} tanggal ${nextRamadan?.toLocaleDateString('en-GB')} Masehi dan ${moment(nextRamadan).format('iDD/iMM/iYYYY')} Hijriah. Tersisa ${timeLeft.days} hari lagi menuju Ramadan.`
                    }
                },
                {
                    "@type": "Question",
                    "name": "Apa saja manfaat puasa Ramadan?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Puasa Ramadan memiliki banyak manfaat, antara lain: memperbaiki kadar darah, mengurangi lemak jahat dalam darah, mengatur tekanan darah, menjaga kadar gula darah, melawan kanker dan peradangan, memperbaiki pencernaan, dan meningkatkan fungsi otak serta mental."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Berapa jumlah hari bulan Ramadan?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Jumlah hari bulan Ramadan berkisar antara 29 hingga 30 hari, tergantung pada penampakan hilal."
                    }
                }
            ]
        };
        // BreadcrumbList Schema
        const breadcrumbSchema = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
                {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Beranda",
                    "item": "https://do-calculate.com/"
                },
                {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Artikel",
                    "item": "https://do-calculate.com/articles-id/"
                },
                {
                    "@type": "ListItem",
                    "position": 3,
                    "name": "Berapa hari lagi Ramadan 2027",
                    "item": OG_URL
                }
            ]
        };
        return [articleSchema, webPageSchema, faqSchema, breadcrumbSchema];
    };
    return (
        <div className="ltr">
            {/* Schema is passed to SharedHelmet and will be added to <head> */}
            <SharedHelmet
                TITLE={TITLE}
                DESCRIPTION={DESCRIPTION}
                KEYWORDS={KEYWORDS}
                OG_URL={OG_URL}
                IMAGE={imageUrl}
                SCHEMA={generateSchemaData()}
            />
            <div className="row">
                <FirstAds />
            </div>
            <h1 className='p-3 mb-2 bg-success text-white'>
                Berapa Hari Lagi Ramadan 2027
            </h1>
            <Suspense fallback={<div>Loading...</div>}>
                <BlinkingText
                    text={`Berapa hari lagi Ramadan: Diperkirakan bulan Ramadan 2027 dimulai hari ${dayNameIndonesian} tanggal ${nextRamadan?.toLocaleDateString('en-GB')} Masehi dan ${moment(nextRamadan).format('iDD/iMM/iYYYY')} menurut kalender Hijriah.`}
                />
            </Suspense>
            <div className="row">
                <div id="timer" style={{ width: '100%', fontSize: '40px' }}>
                    <table style={{ width: '100%', textAlign: 'center', borderCollapse: 'collapse', border: '1px solid #ccc', fontSize: '24px' }}>
                        <thead>
                            <tr>
                                <th>Hari</th>
                                <th>Jam</th>
                                <th>Menit</th>
                                <th>Detik</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td id="days">{timeLeft.days}</td>
                                <td id="hours">{timeLeft.hours}</td>
                                <td id="minutes">{timeLeft.minutes}</td>
                                <td id="seconds">{timeLeft.seconds}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div className='alert alert-info' style={{ marginTop: '20px', marginBottom: '20px', fontSize: '20px', textAlign: 'center' }}>
                    Buka penghitung tasbih (istighfar) dari <a href="/calculator/id/tally-counter/">sini</a>
                </div>
            </div>
            <div className="row">
                <SecondAds />
            </div>
            <div className="row">
                <div className="col-12 col-md-8">
                    <div>
                        <h2 className='p-3 mb-2 bg-success text-white' style={{ color: '#333', fontSize: '36px', textAlign: 'center' }}>Jadwal Ramadan Mendatang</h2>
                        <table style={{ fontSize: '24px', width: '100%', textAlign: 'center', borderCollapse: 'collapse', border: '2px solid #ccc', borderRadius: '8px', overflow: 'hidden' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f2f2f2' }}>
                                    <th style={{ padding: '16px', borderBottom: '2px solid #ccc' }}>Jadwal Ramadan</th>
                                    <th style={{ padding: '16px', borderBottom: '2px solid #ccc' }}>Tanggal</th>
                                    <th style={{ padding: '16px', borderBottom: '2px solid #ccc' }}>Sisa Hari</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ramadanDates.map((ramadan, index) => (
                                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                                        <td style={{ padding: '12px' }}>{ramadan.name}</td>
                                        <td style={{ padding: '12px' }}>{ramadan.date?.toLocaleDateString('en-GB')}</td>
                                        <td style={{ padding: '12px' }}>{calculateTimeLeft(ramadan.date).days} hari</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="col">
                    <Suspense fallback={<div>Loading...</div>}>
                        <MainCalculatorsList />
                    </Suspense>
                </div>
            </div>
            <div className="row">
                <div className="col-12 col-md-8">
                    <h3 className='p-3 mb-2 bg-success text-white'>Kapan Ramadan 2027 Dimulai?</h3>
                    <p>
                        Hanya beberapa bulan lagi menuju bulan Ramadan tahun 2027/1447 H. Perhitungan astronomi menunjukkan bahwa awal bulan yang penuh berkah ini akan jatuh pada hari {dayNameIndonesian} {nextRamadan?.toLocaleDateString('en-GB')} Februari 2027.
                    </p>
                    <p>
                        Pemantauan hilal akan dilakukan setelah terbenamnya matahari pada hari {new Date(nextRamadan.getTime() - 24 * 60 * 60 * 1000).toLocaleDateString('id-ID', { weekday: 'long' })} 29 Syaban 1447, yang diperkirakan bertepatan dengan {new Date(nextRamadan.getTime() - 24 * 60 * 60 * 1000).toLocaleDateString('en-GB')} Februari 2027, untuk menentukan waktu tepat dimulainya puasa.
                    </p>
                    <p>
                        Banyak negara Arab dan Islam bergantung pada rukyatul hilal secara syariat menggunakan observatorium astronomi, sesuai hadits Nabi Muhammad SAW: "Janganlah kalian berpuasa hingga melihat hilal, dan janganlah berbuka hingga melihatnya; jika terhalang awan maka perkirakanlah." Negara-negara Islam diperkirakan akan menyerukan pemantauan hilal pada malam itu, dan pengumuman resmi awal bulan suci akan dilakukan setelah konfirmasi rukyah syar'iyyah.
                    </p>
                    <p>
                        Berdasarkan situs <a href='/calculator/id/gregorian_to_hijri/'>konversi tanggal</a>, Ramadan 2027 bertepatan dengan hari {dayNameIndonesian} tanggal {nextRamadan?.toLocaleDateString('en-GB')} Masehi dan {moment(nextRamadan).format('iDD/iMM/iYYYY')} Hijriah. Anda dapat melihat hitung mundur Ramadan 2027 di artikel ini.
                    </p>
                    <hr />
                    <Suspense fallback={<div>Loading...</div>}>
                        <RelatedLinks />
                    </Suspense>
                    <hr />
                    <h3 className='p-3 mb-2 bg-success text-white'>
                        Berapa Hari Lagi Ramadan 2027 - Hitung Mundur
                    </h3>
                    <p>
                        Melalui hitung mundur Ramadan, Anda dapat mengetahui berapa hari lagi Ramadan dalam kalender Masehi maupun Hijriah, serta mengetahui sisa hari menuju Ramadan. Lihat hitung mundur bulan Ramadan di atas untuk mengetahui waktu tersisa secara rinci (hari, jam, menit, dan detik).
                    </p>
                    <h3 className='p-3 mb-2 bg-success text-white'>
                        Jumlah Jam Puasa di Ramadan 2027
                    </h3>
                    <p>
                        Diperkirakan jumlah jam puasa di negara-negara Islam akan bervariasi selama Ramadan 2027. Pada hari-hari awal, jam puasa akan lebih singkat, sekitar 12 jam di banyak negara Arab termasuk Arab Saudi, Mesir, UEA, Qatar, dan Kuwait. Seiring berjalannya hari, jam puasa bertambah secara bertahap hingga mendekati 13 jam menjelang akhir bulan yang penuh berkah.
                    </p>
                    <p>
                        Umat Muslim di seluruh dunia akan merasakan peningkatan bertahap dalam jam puasa harian; hari pertama Ramadan adalah yang terpendek jam puasanya, sementara hari terakhir memiliki jam puasa terpanjang, mencerminkan perubahan alami panjang siang hari.
                    </p>
                    <h3 className='p-3 mb-2 bg-success text-white'>
                        Ramadan - Bulan yang Penuh Berkah
                    </h3>
                    <p>
                        Bulan Ramadan, bulan kesembilan dalam kalender Hijriah, datang setelah bulan Syaban dan memiliki tempat istimewa di hati umat Muslim. Ini adalah bulan puasa, salah satu rukun Islam, di mana umat Muslim menahan diri dari makan, minum, dan hawa nafsu dari terbit fajar hingga terbenam matahari. Ramadan dimulai dengan penampakan hilal pada hari ke-29 Syaban; jika tidak terlihat, maka Syaban digenapkan 30 hari dan hari berikutnya adalah awal Ramadan. Jumlah hari Ramadan berkisar antara 29 hingga 30 hari sesuai dengan penampakan hilal.
                    </p>
                    <p>
                        Umat Muslim merayakan Idul Fitri setelah berakhirnya Ramadan. Ramadan memiliki tempat istimewa dalam sejarah dan warisan Islam, karena Al-Qur'an diturunkan pada Lailatul Qadar di bulan yang mulia ini. Turunnya wahyu kepada Nabi Muhammad SAW di saat-saat tertentu dalam bulan ini menjadi bukti keagungan dan misi kenabiannya.
                    </p>
                    <div>
                        <br />
                        <img
                            src={imageUrl}
                            alt="Berapa hari lagi Ramadan 2027 - Hitung mundur Ramadan"
                            width="800"
                            height="450"
                            style={{ width: '100%', height: 'auto', maxWidth: '800px' }}
                            loading="lazy"
                        />
                        <br />
                    </div>
                    <h3 className='p-3 mb-2 bg-success text-white'>
                        Manfaat Puasa Ramadan
                    </h3>
                    <p>
                        Puasa Ramadan memiliki banyak manfaat bagi tubuh orang yang berpuasa, mencakup aspek fisik dan psikologis. Jika seseorang mengikuti pola makan sehat selama berpuasa, puasa akan memberikan banyak dampak positif bagi kesehatannya, antara lain memperbaiki kadar darah, mengurangi kadar lemak jahat dalam darah, mengatur tekanan darah, menjaga kadar gula darah, mengurangi stres dan kecemasan, serta melawan penyakit kanker dan peradangan. Berikut ini kami sampaikan berbagai manfaat puasa Ramadan.
                    </p>
                    <ul>
                        <li>Memperbaiki kadar darah</li>
                        <li>Mengurangi kadar lemak jahat dalam darah</li>
                        <li>Mengatur tekanan darah</li>
                        <li>Menjaga kadar gula darah</li>
                        <li>Melawan penyakit kanker dan peradangan</li>
                        <li>Memperbaiki pencernaan dan sistem digestif</li>
                        <li>Meningkatkan fungsi otak dan mental</li>
                    </ul>
                </div>
                <br />
            </div>
        </div>
    );
}
export default Ramadan;
